import { useReducer, useCallback } from 'react';
import { AxiosError } from 'axios';
import { EntityId } from '@restworld/utility-types';
import { createAccountService } from '@restworld/data-services';

import { isValidToken, setSession } from '../utils';
import { initialState, readStoredToken, Types } from '../constants';
import { JWTReducer } from '../reducer';

/**
 * The one failure that means the token is finished.
 *
 * The response interceptor in `data-services` rejects with `{ code, message, data }` when the
 * server answered, and with the raw error when it did not — so a network failure or a timeout has
 * no `code` at all and must never be read as a rejected credential.
 */
const isRejectedCredential = (error: unknown) => (error as { code?: number } | null)?.code === 401;

export const useAuthFunctions = (baseUrl: string) => {
  const [state, dispatch] = useReducer(JWTReducer, initialState);
  // initialize method
  const initialize = useCallback(
    async (jwtArgs?: string) => {
      // Read at call time, never from a copy taken at import: see `readStoredToken`.
      const jwt = jwtArgs || readStoredToken();
      const clearSession = () => {
        setSession(null);
        dispatch({
          type: Types.Initial,
          payload: {
            isAuthenticated: false,
            user: null,
          },
        });
      };

      if (jwt && isValidToken(jwt)) {
        try {
          const user = (await createAccountService({ jwt, baseUrl }).me()).data?.data;
          setSession(jwt);
          dispatch({
            type: Types.Initial,
            payload: {
              isAuthenticated: true,
              user,
              jwt,
            },
          });
        } catch (error) {
          console.error('Error during authentication initialization:', error);

          // Only the server saying the credential is no longer good may destroy it. This used to
          // call `clearSession()` for any failure at all, so one timeout on mobile data deleted a
          // token that was valid for another ten days: an ambassador reported on 07/09/2026 being
          // thrown out three or four times a day, and every one of her tokens carries a 240-hour
          // TTL, so expiry was never what was ending those sessions.
          //
          // Only 401. A 403 means the credential was accepted and the action was not, and a 5xx,
          // a 404 or a network failure say nothing about it at all.
          if (isRejectedCredential(error)) {
            clearSession();
            return;
          }

          // Kept in storage, deliberately NOT put back into state.
          //
          // `state.jwt` is an authorization signal, not a record of what we hold: the shared
          // `AuthGuard` lets a private route render whenever it is truthy, without looking at
          // `isAuthenticated`. Handing it back here would turn a failed credential check into a
          // private page rendering with `user: null` — /profilo would sit on its shell forever,
          // no data, no error, no redirect. Leaving it unset keeps the old, honest outcome for
          // this page view: the guard redirects to the login screen.
          //
          // The whole difference from before is the missing `setSession(null)`. The token is
          // still in storage, so the next load reads it and signs the user back in instead of
          // demanding a fresh login.
          dispatch({
            type: Types.Initial,
            payload: {
              isAuthenticated: false,
              user: null,
            },
          });
        }
      } else {
        clearSession();
      }
    },
    [baseUrl]
  );
  //login method
  const login = useCallback(
    (email: string, password: string) =>
      createAccountService({ baseUrl })
        .login({
          email,
          password,
        })
        .then(async (res) => {
          const { jwt } = res.data;
          setSession(jwt);
          initialize(jwt);
          return res.data;
        })
        .catch((e: AxiosError) => e),
    [baseUrl, initialize]
  );
  //register method
  const signUp = useCallback(
    async (id: string, email: string, password: string) =>
      await createAccountService({ baseUrl })
        .signUp({
          id,
          email,
          password,
        })
        .then((res) => {
          const { jwt } = res.data;
          const user = res.data;
          setSession(jwt);
          dispatch({
            type: Types.Register,
            payload: {
              isAuthenticated: true,
              user,
              jwt,
            },
          });
          return res;
        })
        .catch((error: AxiosError) => {
          dispatch({
            type: Types.Register,
            payload: {
              isAuthenticated: false,
              user: null,
              error,
            },
          });
          return error;
        }),
    [baseUrl]
  );
  //logout method
  const logout = useCallback(async () => {
    await createAccountService({ baseUrl })
      .logout()
      .then(() => {
        setSession(null);
        dispatch({
          type: Types.Logout,
          payload: {
            isAuthenticated: false,
            user: null,
            isReady: true,
            jwt: null,
            logoutRedirect: true,
          },
        });
      })
      .catch((error: AxiosError) => {
        dispatch({
          type: Types.Logout,
          payload: {
            isAuthenticated: false,
            user: null,
            error,
          },
        });
        return error;
      });
  }, [baseUrl]);
  // reset password method
  const resetPassword = useCallback(
    async (id: EntityId, newPassword: string, jwt: string) =>
      await createAccountService({ baseUrl })
        .resetPassword({
          id,
          newPassword,
          jwt,
        })
        .then((res) => {
          const user = res.data;
          const { jwt: accessToken } = res.data;
          setSession(accessToken);
          dispatch({
            type: Types.Login,
            payload: {
              isAuthenticated: true,
              isReady: true,
              user,
              jwt: accessToken,
            },
          });
        }),
    [baseUrl]
  );

  //Verify Token
  const verifyToken = useCallback(
    async (token: string) => {
      await createAccountService({ baseUrl, jwt: token })
        .me()
        .then((response) => {
          const user = response.data.data;
          setSession(token);
          dispatch({
            type: Types.VerifyToken,
            payload: {
              isAuthenticated: true,
              user,
              jwt: token,
              isReady: true,
              loginRedirect: false,
            },
          });
        })
        .catch((error: AxiosError) => {
          setSession(null);
          dispatch({
            type: Types.VerifyToken,
            payload: {
              isAuthenticated: false,
              user: null,
              error,
              isReady: true,
              loginRedirect: true,
            },
          });
        });
    },
    [baseUrl]
  );
  const invalidateSession = () => {
    setSession(null);
    dispatch({
      type: Types.Logout,
      payload: {
        isAuthenticated: false,
        user: null,
        isReady: true,
        jwt: null,
        logoutRedirect: true,
      },
    });
  };

  return {
    state,
    login,
    logout,
    signUp,
    resetPassword,
    initializeAuthentication: initialize,
    verifyToken,
    invalidateSession,
    initialize,
  };
};
